import { useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';

// One <video> element per MediaStream for the whole call (M70, D267). The
// full call screen, the floating tile and the picture-in-picture window all
// borrow the same element, so moving between them never re-attaches the
// stream, renegotiates or asks for the camera again. A media element moved
// to a new parent within the same task keeps playing (HTML: it is only
// paused if it is still out of the document at the next stable state).
const videos = new WeakMap<MediaStream, HTMLVideoElement>();

export function videoForStream(stream: MediaStream): HTMLVideoElement {
  let video = videos.get(stream);
  if (!video) {
    video = document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    // Sound comes from CallAudio, which never moves; the picture is muted
    // so nobody hears a person twice.
    video.muted = true;
    video.srcObject = stream;
    videos.set(stream, video);
  }
  return video;
}

function play(video: HTMLVideoElement) {
  try {
    const started = video.play() as Promise<void> | undefined;
    started?.catch(() => undefined);
  } catch {
    // jsdom and very old browsers.
  }
}

/** Shows a stream's shared <video> element in this spot. */
export function StreamVideo({
  stream,
  mirror,
  fit = 'cover',
  style,
}: {
  stream: MediaStream;
  mirror?: boolean;
  fit?: 'cover' | 'contain';
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Layout effect: runs in the same commit that removed the element from
  // its old spot, so it is never out of the document long enough to pause.
  useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;
    const video = videoForStream(stream);
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = fit;
    video.style.display = 'block';
    video.style.transform = mirror ? 'scaleX(-1)' : '';
    if (video.parentNode !== host) host.appendChild(video);
    if (video.paused) play(video);
    return () => {
      if (video.parentNode === host) host.removeChild(video);
    };
  }, [stream, mirror, fit]);
  return (
    <div
      ref={ref}
      style={{ width: '100%', height: '100%', overflow: 'hidden', ...style }}
    />
  );
}

function StreamAudio({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.srcObject = stream;
    return () => {
      element.srcObject = null;
    };
  }, [stream]);
  return <audio ref={ref} autoPlay />;
}

/**
 * Everyone's voice, in one place that stays mounted for the whole call: the
 * full screen, the tile and picture-in-picture come and go around it, so
 * audio never stops (D267), and nothing here pauses when the page is hidden
 * (D268).
 */
export function CallAudio({
  streams,
}: {
  streams: Record<string, MediaStream>;
}) {
  return (
    <div style={{ display: 'none' }} data-call-audio="">
      {Object.entries(streams).map(([id, stream]) => (
        <StreamAudio key={id} stream={stream} />
      ))}
    </div>
  );
}
